/**
 * Core Connect Four Game Types
 *
 * This file defines all TypeScript types and interfaces for the Connect Four
 * game implementation. Grid convention: grid[row][col], row 0 = bottom,
 * row 5 = top, col 0 = leftmost, col 6 = rightmost.
 *
 * Requirements: 1.1, 1.5, 12.1, 42.1, 42.2, 42.3, 42.4
 */

// ============================================================================
// Basic Connect Four Types
// ============================================================================

/** Cell state: red disc, yellow disc, or empty */
export type CellState = 'red' | 'yellow' | null;

/** 6 rows × 7 columns grid. grid[row][col], row 0 = bottom, row 5 = top */
export type ConnectFourGrid = CellState[][];

/** Disc colors (players) */
export type DiscColor = 'red' | 'yellow';

/** AI difficulty levels */
export type ConnectFourDifficulty = 'easy' | 'medium' | 'hard';

/** Position on the board */
export interface Position {
  row: number; // 0-5 (0 = bottom)
  col: number; // 0-6
}

// ============================================================================
// Move Types
// ============================================================================

/** A single move in Connect Four */
export interface ConnectFourMove {
  column: number;      // 0-6
  row: number;         // 0-5 (computed: lowest empty row)
  player: DiscColor;   // 'red' | 'yellow'
  moveNumber: number;  // 1-based move number
  timestamp: number;
}

/** Result of a move attempt */
export interface ConnectFourMoveResult {
  success: boolean;
  move?: ConnectFourMove;
  error?: string;
  gameStatus: ConnectFourGameStatus;
}

// ============================================================================
// Game State Types
// ============================================================================

/** Game status */
export type ConnectFourGameStatus =
  | { type: 'active' }
  | { type: 'win'; winner: DiscColor; direction: 'horizontal' | 'vertical' | 'diagonal-asc' | 'diagonal-desc' }
  | { type: 'draw' }
  | { type: 'resignation'; winner: DiscColor };

/** Complete game state for persistence, synchronization, and serialization */
export interface ConnectFourGameState {
  grid: ConnectFourGrid;
  currentPlayer: DiscColor;
  moveHistory: ConnectFourMove[];
  gameStatus: ConnectFourGameStatus;
  moveCount: number;
  winningCells: Position[] | null;
}

// ============================================================================
// Game Settings Types
// ============================================================================

/** Game settings for mode selection */
export interface ConnectFourGameSettings {
  mode: 'singleplayer' | 'local-multiplayer' | 'multiplayer-realtime';
  aiDifficulty?: ConnectFourDifficulty;
  isPrivate?: boolean;
}

// ============================================================================
// Game Result Types
// ============================================================================

/** Game result for statistics tracking */
export interface ConnectFourGameResult {
  gameId: string;
  mode: ConnectFourGameSettings['mode'];
  difficulty?: ConnectFourDifficulty;
  winner?: DiscColor;
  isDraw: boolean;
  reason: string;
  moveCount: number;
  duration: number; // seconds
  completedAt: number;
}

// ============================================================================
// Statistics Types
// ============================================================================

/** Game statistics for a specific category */
export interface GameStats {
  wins: number;
  losses: number;
  draws: number;
  winPercentage: number;
  averageGameLength: number; // moves
}

/** Complete Connect Four statistics */
export interface ConnectFourStatistics {
  userId?: string;
  singlePlayer: {
    easy: GameStats;
    medium: GameStats;
    hard: GameStats;
  };
  localMultiplayer: GameStats;
  onlineMultiplayer: GameStats;
  totalGames: number;
  totalPlayTime: number; // seconds
}

// ============================================================================
// Component Props Types
// ============================================================================

/** ConnectFourBoard component props */
export interface ConnectFourBoardProps {
  engine: any; // ConnectFourEngine (avoid circular import)
  interactive?: boolean;
  isAIThinking?: boolean;
  onMove?: (move: ConnectFourMove) => void;
  onGameEnd?: (result: ConnectFourGameResult) => void;
  moveCount?: number;
  playerColor?: DiscColor;
}

/** ConnectFourBoard internal state */
export interface ConnectFourBoardState {
  hoveredColumn: number | null;
  previewRow: number | null;
  lastMove?: ConnectFourMove;
  winningCells: Position[];
  animatingDrop?: {
    column: number;
    targetRow: number;
    color: DiscColor;
    currentY: number;
  };
}

/** ConnectFourGameModeSelector props */
export interface ConnectFourGameModeSelectorProps {
  onStartGame: (settings: ConnectFourGameSettings) => void;
  onJoinPrivateGame: (roomCode: string) => Promise<void>;
  guestGamesRemaining?: number;
  isAuthenticated?: boolean;
}

/** MoveHistory props for Connect Four */
export interface ConnectFourMoveHistoryProps {
  moves: ConnectFourMove[];
  currentMoveIndex?: number;
}

// ============================================================================
// Utility Functions
// ============================================================================

/** Number of rows in the Connect Four grid */
export const ROWS = 6;

/** Number of columns in the Connect Four grid */
export const COLS = 7;

/** Total number of cells */
export const TOTAL_CELLS = ROWS * COLS; // 42

/** Check if a position is within the board bounds */
export function isValidPosition(pos: Position): boolean {
  return pos.row >= 0 && pos.row < ROWS && pos.col >= 0 && pos.col < COLS;
}

/** Check if two positions are equal */
export function positionsEqual(a: Position, b: Position): boolean {
  return a.row === b.row && a.col === b.col;
}
